import {
  Caption,
  css,
  HelpIcon,
  Inline,
  Mode,
  SelectInput,
  styled,
  Tooltip,
  useModeContext,
} from 'canoe';
import React, { ReactNode } from 'react';

import { BoxContainer } from './BoxContainer';

const BoxLabel = styled(Caption)<{ mode?: Mode }>`
  ${({ theme, mode }) => css`
    flex-shrink: 0;
    max-width: none;
    height: 20px;
    margin: 0;
    padding: 0 ${theme.spaces.xs};
    color: ${theme.colors.OnyxGrayD10};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    user-select: none;
    flex: auto;

    ${mode === 'Dark' &&
    css`
      color: ${theme.colors.White};
    `}
  `}
`;

const BoxChildren = styled.div<{ hasBoxLabel?: boolean; allowGrow: boolean }>`
  ${({ hasBoxLabel, allowGrow }) => css`
    max-width: 100%;
    flex-shrink: 0;
    flex-grow: ${allowGrow ? '1' : '0'};
    display: flex;
    align-items: center;
    justify-content: ${hasBoxLabel ? 'flex-end' : 'center'};
  `}
`;

export type AtLeastOne<T> = Partial<T> & { [K in keyof T]: Pick<T, K> }[keyof T];

interface ContextProps {
  mode?: Mode;
  isExpanded?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isInGroup?: boolean;
  isExpandable?: boolean;
  responsiveWidth?: boolean;
  hasBoxLabel?: boolean;
}

export type ValueBoxGroupOptions =
  | boolean
  | AtLeastOne<{
      isFirst: boolean;
      isLast: boolean;
    }>;

export interface ValueBoxProps {
  children: ReactNode;
  label?: string | ReactNode;
  id?: string;
  mode?: Mode;
  isExpandable?: {
    isExpanded: boolean;
  };
  isInGroup?: ValueBoxGroupOptions;
  responsiveToggle?: boolean;
  className?: string;
  'data-cy'?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  showOverflow?: boolean;
  noBorder?: boolean;
  tooltip?: string;
  options?: { label: string; value: string }[];
  onOptionChange?: (value: string) => void;
  allowChildrenGrow?: boolean;
}

export const { Provider: ValueBoxProvider, Consumer: ValueBoxConsumer } = React.createContext<
  ContextProps
>({});

export const ValueBox = React.memo(
  ({
    children,
    label,
    id,
    mode,
    isInGroup: groupData,
    isExpandable: expandableData,
    responsiveToggle,
    className,
    'data-cy': dataCy,
    onClick,
    onMouseEnter,
    onMouseLeave,
    showOverflow,
    noBorder,
    tooltip,
    options,
    onOptionChange,
    allowChildrenGrow = true,
  }: ValueBoxProps) => {
    const { mode: modeFromContext } = useModeContext();

    const contextValues: ContextProps = {
      isExpanded: undefined,
      isFirst: undefined,
      isLast: undefined,
      isInGroup: undefined,
      isExpandable: undefined,
      mode: mode || modeFromContext,
      responsiveWidth: responsiveToggle,
      hasBoxLabel: !!label || (!!label && !!options),
    };

    if (groupData !== undefined) {
      if (typeof groupData === 'boolean') {
        contextValues.isInGroup = groupData;
      } else if (typeof groupData === 'object') {
        contextValues.isInGroup = Object.keys(groupData).length > 0;
        contextValues.isFirst = groupData.isFirst;
        contextValues.isLast = groupData.isLast;
      }
    }

    if (expandableData !== undefined) {
      if (Object.keys(expandableData).length > 0) {
        contextValues.isExpandable = true;
      }
      if (expandableData.isExpanded) {
        contextValues.isExpanded = true;
      }
    }

    return (
      <ValueBoxProvider value={contextValues}>
        <BoxContainer
          id={id}
          data-cy={dataCy}
          {...contextValues}
          className={className}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          showOverflow={showOverflow}
          noBorder={noBorder}
        >
          {(!!label || (!!label && !!options)) && (
            <BoxLabel mode={mode || modeFromContext}>
              {options ? (
                <SelectInput<{ label: string; value: string }>
                  triggerAppearance="inline"
                  scale="small"
                  clearable={false}
                  items={options}
                  selectedItem={options.find((item) => item.label === label)}
                  renderValue={(valueProps) => (
                    <Inline space="xxs" align="center" fillSpace>
                      <span>{valueProps.selectedItem.label}</span>
                      {tooltip && (
                        <Tooltip content={tooltip} placement="top">
                          <HelpIcon color="OnyxGrayL30" size="xs" />
                        </Tooltip>
                      )}
                    </Inline>
                  )}
                  onSelect={(selected) => selected && onOptionChange?.(selected.value)}
                />
              ) : (
                <Inline space="xxs" align="center" fillSpace>
                  <span>{label}</span>
                  {tooltip && (
                    <Tooltip content={tooltip} placement="top">
                      <HelpIcon color="OnyxGrayL30" size="xs" />
                    </Tooltip>
                  )}
                </Inline>
              )}
            </BoxLabel>
          )}
          <BoxChildren hasBoxLabel={contextValues.hasBoxLabel} allowGrow={allowChildrenGrow}>
            {children}
          </BoxChildren>
        </BoxContainer>
      </ValueBoxProvider>
    );
  },
);
